const COLLISION_GAP_PX = 4;
const RANDOM_ATTEMPTS = 96;
const GRID_COLUMNS = 12;
const GRID_ROWS = 8;

const finiteNonNegative = (value) => {
  const number = value - 0;
  return number > 0 && Math.abs(number) !== Infinity ? number : 0;
};

const clampUnit = value => Math.max(0, Math.min(1, (value - 0) || 0));

function intersectionArea(left, right) {
  const overlapWidth = Math.min(left.x + left.width, right.x + right.width)
    - Math.max(left.x, right.x);
  const overlapHeight = Math.min(left.y + left.height, right.y + right.height)
    - Math.max(left.y, right.y);
  return overlapWidth > 0 && overlapHeight > 0 ? overlapWidth * overlapHeight : 0;
}

function scoreCandidate(candidate, occupied) {
  return occupied.reduce((score, rectangle) => {
    const expanded = {
      x: rectangle.x - COLLISION_GAP_PX,
      y: rectangle.y - COLLISION_GAP_PX,
      width: rectangle.width + (COLLISION_GAP_PX * 2),
      height: rectangle.height + (COLLISION_GAP_PX * 2),
    };
    const area = intersectionArea(candidate, expanded);
    if (area > 0) {
      score.totalOverlapArea += area;
      score.intersectionCount += 1;
      score.largestOverlapArea = Math.max(score.largestOverlapArea, area);
    }
    return score;
  }, {
    totalOverlapArea: 0,
    intersectionCount: 0,
    largestOverlapArea: 0,
  });
}

function compareCandidate(left, right) {
  return left.totalOverlapArea - right.totalOverlapArea
    || left.intersectionCount - right.intersectionCount
    || left.largestOverlapArea - right.largestOverlapArea
    || left.index - right.index;
}

function normalizeOccupiedRectangles(value, bounds) {
  if (!Array.isArray(value)) return [];
  return value.reduce((rectangles, rectangle) => {
    if (!rectangle) return rectangles;
    const x = Math.min(finiteNonNegative(rectangle.x), bounds.width);
    const y = Math.min(finiteNonNegative(rectangle.y), bounds.height);
    const width = Math.min(finiteNonNegative(rectangle.width), bounds.width - x);
    const height = Math.min(finiteNonNegative(rectangle.height), bounds.height - y);
    if (width > 0 && height > 0) {
      rectangles.push({
        x,
        y,
        width,
        height,
      });
    }
    return rectangles;
  }, []);
}

function placeRectangle(options = {}) {
  const bounds = {
    width: finiteNonNegative(options.bounds && options.bounds.width),
    height: finiteNonNegative(options.bounds && options.bounds.height),
  };
  const size = {
    width: Math.min(finiteNonNegative(options.size && options.size.width), bounds.width),
    height: Math.min(finiteNonNegative(options.size && options.size.height), bounds.height),
  };
  const occupied = normalizeOccupiedRectangles(options.occupied, bounds);
  const random = typeof options.random === 'function' ? options.random : Math.random;
  const maxX = bounds.width - size.width;
  const maxY = bounds.height - size.height;
  let best = null;
  let candidateIndex = 0;
  const consider = (x, y) => {
    const candidate = {
      x,
      y,
      width: size.width,
      height: size.height,
    };
    const ranked = Object.assign(
      { index: candidateIndex },
      candidate,
      scoreCandidate(candidate, occupied),
    );
    candidateIndex += 1;
    if (!best || compareCandidate(ranked, best) < 0) best = ranked;
  };

  for (let index = 0; index < RANDOM_ATTEMPTS; index += 1) {
    consider(
      Math.round(clampUnit(random()) * maxX),
      Math.round(clampUnit(random()) * maxY),
    );
  }
  for (let row = 0; row < GRID_ROWS; row += 1) {
    for (let column = 0; column < GRID_COLUMNS; column += 1) {
      consider(
        Math.round((column / (GRID_COLUMNS - 1)) * maxX),
        Math.round((row / (GRID_ROWS - 1)) * maxY),
      );
    }
  }

  return {
    x: best.x,
    y: best.y,
    width: size.width,
    height: size.height,
    mode: best.intersectionCount === 0 ? 'open' : 'overlap',
  };
}

module.exports = { placeRectangle };
